import { useState, useRef, useEffect } from 'react';
import { C, t } from '../constants/dashboardConstants';
import Avatar from './Avatar';

function DateDivider({ date }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
      <div style={{ flex: 1, height: '1px', background: C.g200 }} />
      <span style={t('14px', 500, C.sub)}>{date}</span>
      <div style={{ flex: 1, height: '1px', background: C.g200 }} />
    </div>
  );
}

function MessageBubble({ msg, onRetry }) {
  if (msg.type === 'date') return <DateDivider date={msg.text} />;

  const isMine = msg.isMine;
  const failed = msg.status === 'failed';
  const sending = msg.status === 'sending';

  return (
    <div
      data-mine={isMine ? 'true' : 'false'}
      aria-label={isMine ? '내가 보낸 메시지' : '상대가 보낸 메시지'}
      style={{ display: 'flex', flexDirection: 'column', alignItems: isMine ? 'flex-end' : 'flex-start', gap: '10px' }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: '10px', flexDirection: isMine ? 'row-reverse' : 'row' }}>
        <div style={{
          padding: '24px',
          background: isMine ? C.bk : C.bg,
          border: isMine ? 'none' : `1px solid ${C.g200}`,
          borderRadius: '20px',
          maxWidth: '500px',
          opacity: sending ? 0.6 : 1,
        }}>
          <span style={{ ...t('18px', 400, isMine ? C.white : C.bk), whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {msg.text}
          </span>
        </div>
        <span style={t('14px', 400, C.sub)}>
          {failed ? '전송 실패' : sending ? '전송 중' : msg.time}
        </span>
      </div>
      {failed && onRetry && (
        <button
          onClick={() => onRetry(msg)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, ...t('14px', 600, '#EF4444') }}
        >
          다시 시도
        </button>
      )}
    </div>
  );
}

/** 공고에서 시작된 대화일 때만 뜨는 문맥 헤더 */
function PostingContextHeader({ posting, onOpen }) {
  if (!posting) return null;
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px',
      padding: '18px 20px', background: C.white, borderBottom: `1px solid ${C.g200}`, flexShrink: 0,
    }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', minWidth: 0 }}>
        <span style={{ ...t('18px', 600, C.bk), overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {posting.title}
        </span>
        {posting.location && <span style={t('14px', 400, C.sub)}>{posting.location}</span>}
      </div>
      <button
        onClick={onOpen}
        style={{
          flexShrink: 0, padding: '10px 18px', borderRadius: '10px',
          border: `1px solid ${C.g200}`, background: C.white, cursor: 'pointer', ...t('14px', 600, C.bk),
        }}
      >
        공고 확인
      </button>
    </div>
  );
}

/**
 * 대화 화면 — 상태를 갖지 않는 제어형 컴포넌트.
 * 메시지 로딩·전송은 상위(MessagesView)가 담당한다.
 */
export default function ChatWindow({
  conversation,
  messages = [],
  onSend,
  onRetry,
  onOpenPosting,
  readOnly = false,
  readOnlyNotice,
  loading = false,
}) {
  const [input, setInput] = useState('');
  const bottomRef = useRef(null);

  // 대화를 바꾸면 입력창을 비운다 (이전 대화에 쓰던 글이 남지 않도록)
  useEffect(() => { setInput(''); }, [conversation?.id]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  const handleSend = () => {
    const text = input.trim();
    if (!text || readOnly) return;
    setInput('');
    onSend?.(text);
  };

  if (!conversation) {
    return (
      <div style={{
        flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
        border: `1px solid ${C.g200}`, borderRadius: '20px', background: C.white, boxShadow: C.shadow,
      }}>
        <span style={t('16px', 400, C.sub)}>대화를 선택하세요.</span>
      </div>
    );
  }

  return (
    <div style={{
      flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column',
      border: `1px solid ${C.g200}`, borderRadius: '20px', overflow: 'hidden',
      boxShadow: C.shadow,
    }}>
      {/* 상대 정보 */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '14px',
        padding: '20px', height: '90px', boxSizing: 'border-box',
        background: C.white, borderBottom: `1px solid ${C.g200}`, flexShrink: 0,
      }}>
        <Avatar url={conversation.avatarUrl} name={conversation.company} size={50} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: '11px', minWidth: 0 }}>
          <span style={t('18px', 600, C.bk)}>{conversation.company}</span>
          <span style={t('14px', 400, C.sub)}>{conversation.project}</span>
        </div>
      </div>

      {/* 공고 문맥 — 공고에서 시작된 대화에만 */}
      <PostingContextHeader posting={conversation.posting} onOpen={onOpenPosting} />

      {/* 메시지 */}
      <div style={{
        flex: 1, overflowY: 'auto', padding: '30px',
        display: 'flex', flexDirection: 'column', gap: '30px',
        background: C.white,
      }}>
        {loading && messages.length === 0 ? (
          <span style={{ ...t('16px', 400, C.sub), textAlign: 'center' }}>불러오는 중...</span>
        ) : (
          messages.map(msg => <MessageBubble key={msg.id} msg={msg} onRetry={onRetry} />)
        )}
        <div ref={bottomRef} />
      </div>

      {/* 입력 */}
      {readOnly ? (
        <div style={{
          padding: '24px 20px', background: C.bg, flexShrink: 0,
          borderTop: `1px solid ${C.g200}`, textAlign: 'center',
        }}>
          <span style={t('16px', 400, C.sub)}>
            {readOnlyNotice ?? '차단한 대화입니다. 차단을 해제하면 다시 보낼 수 있습니다.'}
          </span>
        </div>
      ) : (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '8px',
          padding: '20px 14px', background: C.bg, flexShrink: 0, height: '100px', boxSizing: 'border-box',
        }}>
          <button
            title="파일 첨부 (준비 중)"
            aria-label="파일 첨부"
            disabled
            style={{
              width: '60px', height: '60px', borderRadius: '10px',
              background: 'transparent', border: 'none', cursor: 'not-allowed', flexShrink: 0, opacity: 0.4,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48"
                stroke={C.g500} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>

          <input
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSend()}
            placeholder="직접 입력"
            style={{
              flex: 1, height: '60px', padding: '0 18px',
              background: C.white, border: `1px solid ${C.g200}`, borderRadius: '10px',
              ...t('18px', 400, C.bk), outline: 'none',
            }}
          />

          <button
            onClick={handleSend}
            disabled={!input.trim()}
            aria-label="전송" 
            style={{
              width: '60px', height: '60px', borderRadius: '10px',
              background: input.trim() ? C.bk : C.g200, border: 'none',
              cursor: input.trim() ? 'pointer' : 'default', flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M22 2L11 13M22 2L15 22l-4-9-9-4 20-7z" stroke={C.white} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
      )}
    </div>
  );
}
